import { FC, useEffect, useRef } from "react";
import { useCookies } from "react-cookie";
import useSnackbar from "../../context/Snackbar/useSnackbar";
import { drainPendingSales, loadPendingSales } from "../../utils/offlineSalesQueue";

// docs/pos-cashier-operations-plan.md PR 5 — offline-resilient checkout.
// Always-mounted (alongside ChatSocketProvider in App.tsx), renders
// nothing — just drains CheckoutPage's localStorage-backed pending-sales
// queue whenever the browser regains connectivity, plus a periodic
// fallback in case the `online` event doesn't fire reliably (some browsers
// only fire it on a genuine interface change, not a flaky captive-portal-
// style connection). A `pendingSalesChanged` event (dispatched by
// offlineSalesQueue on every mutation) is what CheckoutPage's own pending-
// count badge listens to — this component only needs cookies.auth to
// actually make the replay requests.
const FALLBACK_INTERVAL_MS = 30000;

const PendingSalesSyncManager: FC = () => {
  const [cookies] = useCookies();
  const snack = useSnackbar();
  const isDrainingRef = useRef(false);

  useEffect(() => {
    const drain = async () => {
      if (isDrainingRef.current) return;
      if (loadPendingSales().length === 0) return;
      if (!cookies.auth?.accessToken) return; // not logged in yet — nothing to auth the replay with
      isDrainingRef.current = true;
      try {
        await drainPendingSales(cookies.auth, (sale, orderId) => {
          snack.onResponse({ message: `Synced queued sale — Order #${orderId}`, status: 200 });
        });
      } finally {
        isDrainingRef.current = false;
      }
    };

    drain();
    window.addEventListener("online", drain);
    const interval = setInterval(drain, FALLBACK_INTERVAL_MS);
    return () => {
      window.removeEventListener("online", drain);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookies.auth?.accessToken]);

  return null;
};

export default PendingSalesSyncManager;
